import { ApiRequestError } from "@shyne/shared/api";
import { SessionUser } from "@/models/user.model.ts";
import supabase from "@/lib/supabase.ts";
import { LoginPayload, LoginResponse } from "@/app/auth/login/login.entity.ts";
import { uuid } from "@/interfaces";
import { recordAuthEmailEvent } from "@/services/auth-email-events.service";
import { api } from "@/lib/api";
import { toSessionUser } from "@/repositories/profile-records";

export const AuthRepository = {
  async signupPending(): Promise<boolean> {
    return (await api.me()).userId === null;
  },

  async getSessionUser(authId: uuid): Promise<SessionUser | null> {
    try {
      return toSessionUser(await api.profile.get(), authId);
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 404) return null;
      throw error;
    }
  },

  async loginWithEmail({ email, password }: LoginPayload): Promise<LoginResponse> {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error || !data.user || !data.session) {
      if (error?.message?.includes("confirmation") || error?.message?.includes("Email not confirmed")) {
        throw new Error("Email not confirmed. Please click the link we sent you.");
      }

      throw new Error("Invalid email or password");
    }

    const user = await AuthRepository.getSessionUser(data.user.id);

    return {
      user_id: user?.id ?? null,
      auth_id: data.user.id,
      session: data.session.access_token,
    };
  },

  async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();

    if (error) throw error;
  },

  async resetPasswordForEmail(email: string, redirectTo: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });

    if (error) {
      void recordAuthEmailEvent({
        eventType: "auth_password_reset",
        recipientEmail: email,
        status: "failed",
        payload: { redirectTo },
        errorMessage: error.message,
      });
      throw error;
    }

    void recordAuthEmailEvent({
      eventType: "auth_password_reset",
      recipientEmail: email,
      payload: { redirectTo },
    });
  },

  async updatePassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) throw error;
  },

  async changePassword(email: string, currentPassword: string, newPassword: string): Promise<void> {
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password: currentPassword,
    });

    if (signInError) {
      throw new Error("Current password is incorrect");
    }

    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (updateError) throw updateError;
  },
};
